import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, InputGroup } from '@/components/ui';
// import { materialService, MaterialSearchResult } from '../../../services/materialService'; // Temporarily removed - to be re-added when dropdown is implemented

interface FieldMapping {
  sourceField: string;
  targetField: string;
}

interface DataSource {
  type: 'csv' | 'search';
  file?: string;
  valueField: string;
  displayField: string;
  mappings: FieldMapping[];
}

interface FieldConfig {
  field: string;
  type: 'text' | 'dropdown';
  dataSource?: DataSource;
}

interface EditableFieldProps {
  fieldId: number;
  fieldName: string;
  value: string;
  onSave: (fieldId: number, value: string, additionalUpdates?: { fieldId: number; value: string }[]) => Promise<void>;
  config?: FieldConfig;
  form: any; // The complete form object to access other fields
  disabled?: boolean;
}

const FIELD_CONFIGS: { [key: string]: FieldConfig } = {
  // MaterialUsed 4 is now a regular text field - dropdown functionality to be added later
  // 'MaterialUsed 4': {
  //   field: 'MaterialUsed 4',
  //   type: 'dropdown',
  //   dataSource: {
  //     type: 'search',
  //     valueField: 'materialUsed',
  //     displayField: 'materialUsed',
  //     mappings: [
  //       {
  //         sourceField: 'rate',
  //         targetField: 'MaterialRate'
  //       },
  //       {
  //         sourceField: 'unitOfMeasure',
  //         targetField: 'UnitOfMeasure'
  //       }
  //     ]
  //   }
  // }
};

// Debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const EditableField: React.FC<EditableFieldProps> = ({
  fieldId,
  fieldName,
  value,
  onSave,
  config,
  form,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [options, setOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const fieldConfig = config || FIELD_CONFIGS[fieldName];

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Search materials function
  const searchMaterials = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // const response = await materialService.searchMaterials(query, 50);
      // setOptions(response.results);
      setOptions([]); // Temporarily disabled - to be re-enabled when dropdown is implemented
    } catch (error) {
      console.error('Error searching materials:', error);
      setError('Failed to search materials');
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect for debounced search
  useEffect(() => {
    if (fieldConfig?.dataSource?.type === 'search' && debouncedSearchTerm) {
      searchMaterials(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, fieldConfig, searchMaterials]);

  // Initialize search term when editing starts
  useEffect(() => {
    if (isEditing && fieldConfig?.dataSource?.type === 'search') {
      setSearchTerm(editValue || '');
      if (editValue && editValue.length >= 2) {
        searchMaterials(editValue);
      }
    }
  }, [isEditing, editValue, fieldConfig, searchMaterials]);

  const handleSave = async () => {
    if (fieldConfig?.dataSource?.mappings) {
      // Find the selected option data
      const selectedOption = options.find(opt => 
        opt[fieldConfig.dataSource!.valueField] === editValue
      );

      if (selectedOption) {
        // Prepare additional updates based on mappings
        const additionalUpdates = fieldConfig.dataSource.mappings
          .map(mapping => {
            // Find the target field in the form
            const targetField = form.fields.find((f: any) => {
              // Check if the field name matches and is in the same section/group
              const isMatchingField = f.field === mapping.targetField;
              const isSameGroup = f.path.split('.').slice(0, -1).join('.') === 
                                form.fields.find((ff: any) => ff.id === fieldId)?.path.split('.').slice(0, -1).join('.');
              return isMatchingField && isSameGroup;
            });

            if (targetField) {
              const sourceValue = selectedOption[mapping.sourceField];
              return {
                fieldId: targetField.id,
                value: sourceValue?.toString() || ''
              };
            }
            return null;
          })
          .filter((update): update is { fieldId: number; value: string } => update !== null);

        await onSave(fieldId, editValue, additionalUpdates);
      } else {
        await onSave(fieldId, editValue);
      }
    } else {
      await onSave(fieldId, editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    setSearchTerm('');
    setOptions([]);
    setError(null);
  };

  if (!isEditing) {
    return (
      <div
        className="editable-cell tw-flex tw-justify-between tw-items-center"
        onClick={() => !disabled && setIsEditing(true)}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      >
        <span title={value || '-'}>{value || '-'}</span>
        {!disabled && <i className="bi bi-pencil tw-text-content-muted tw-opacity-50" />}
      </div>
    );
  }

  if (fieldConfig?.type === 'dropdown' && fieldConfig?.dataSource?.type === 'search') {
    return (
      <InputGroup>
        <div className="tw-relative tw-w-full">
          <Form.Control
            type="text"
            size="sm"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setEditValue(e.target.value);
            }}
            placeholder={`Search ${fieldName}...`}
            autoFocus
          />

          {/* Dropdown with search results */}
          {(isLoading || options.length > 0 || error) && (
            <div
              className="tw-absolute tw-w-full tw-bg-surface tw-border tw-border-line tw-border-t-0 tw-shadow-sm"
              style={{
                top: '100%',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto'
              }}
            >
              {isLoading && (
                <div className="tw-p-2 tw-text-center tw-text-content-muted">
                  <small>Searching...</small>
                </div>
              )}

              {error && (
                <div className="tw-p-2 tw-text-center tw-text-danger">
                  <small>{error}</small>
                </div>
              )}

              {!isLoading && !error && options.length === 0 && searchTerm.length >= 2 && (
                <div className="tw-p-2 tw-text-center tw-text-content-muted">
                  <small>No materials found</small>
                </div>
              )}

              {!isLoading && !error && options.map((option, index) => (
                <div
                  key={index}
                  className="tw-p-2 tw-border-b tw-border-line tw-cursor-pointer hover-bg-light"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const materialValue = option[fieldConfig.dataSource!.valueField];
                    setEditValue(materialValue);
                    setSearchTerm(materialValue);
                    setOptions([]);
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-text-black">{option.materialUsed}</span>
                    <span className="tw-text-content-muted tw-text-sm">{option.unitOfMeasure} @ ${option.rate}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          variant="success"
          size="sm"
          onClick={handleSave}
          disabled={isLoading}
        >
          <i className="bi bi-check"></i>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCancel}
          disabled={isLoading}
        >
          <i className="bi bi-x"></i>
        </Button>
      </InputGroup>
    );
  }

  // Original dropdown implementation for CSV files (fallback)
  if (fieldConfig?.type === 'dropdown') {
    return (
      <InputGroup>
        {isLoading ? (
          <div className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1 tw-text-sm tw-text-center">
            <small>Loading materials...</small>
          </div>
        ) : error ? (
          <div className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1 tw-text-sm tw-text-center tw-text-danger">
            <small>{error}</small>
          </div>
        ) : (
          <Form.Select
            size="sm"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          >
            <option value="">Select {fieldName}</option>
            {options.map((option, index) => (
              <option
                key={index}
                value={option[fieldConfig.dataSource!.valueField]}
              >
                {option[fieldConfig.dataSource!.displayField]}
              </option>
            ))}
          </Form.Select>
        )}
        <Button
          variant="success"
          size="sm"
          onClick={handleSave}
          disabled={isLoading}
        >
          <i className="bi bi-check"></i>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCancel}
          disabled={isLoading}
        >
          <i className="bi bi-x"></i>
        </Button>
      </InputGroup>
    );
  }

  return (
    <InputGroup>
      <Form.Control
        type="text"
        size="sm"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
      />
      <Button
        variant="success"
        size="sm"
        onClick={handleSave}
      >
        <i className="bi bi-check"></i>
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleCancel}
      >
        <i className="bi bi-x"></i>
      </Button>
    </InputGroup>
  );
};

export default EditableField; 